import { BookmarkCheck, AlertTriangle, ArrowRight, Scale, Target, Hash } from 'lucide-react';
import ConceptMap from './ConceptMap';
import ProtectedContentImage from './ProtectedContentImage';
import SourceAttribution from './SourceAttribution';

function InlineContent({ text = '', links = [], onSectionSelect }) {
  const linkByLabel = new Map(links.filter((link) => link?.label && link?.section_id).map((link) => [link.label, link]));
  const tokens = String(text).split(/(\*\*[^*]+\*\*)/g);
  return tokens.map((token, index) => {
    const strong = token.startsWith('**') && token.endsWith('**');
    const label = strong ? token.slice(2, -2) : token;
    const link = linkByLabel.get(label);
    if (link) {
      return <button key={index} type="button" onClick={() => onSectionSelect?.(link.section_id)} className="key-concept-link">{label}</button>;
    }
    return strong ? <strong key={index} className="font-semibold text-brand-dark">{label}</strong> : token;
  });
}

export function EditorialTable({ block, index }) {
  const variant = block.presentation === 'numbers' ? 'numbers' : block.presentation === 'comparison' ? 'comparison' : 'table';
  const title = block.title || (variant === 'numbers' ? 'Numeri da ricordare' : variant === 'comparison' ? 'Confronto rapido' : null);
  return (
    <section className={`editorial-table editorial-table--${variant}`} data-testid={`${variant}-table-${index}`}>
      {title && <h3 className="editorial-table__title">{title}</h3>}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr>{(block.headers || []).map((header, cell) => <th key={cell} scope="col">{header}</th>)}</tr></thead>
          <tbody>{(block.rows || []).map((row, rowIndex) => <tr key={rowIndex}>{row.map((value, cell) => <td key={cell} data-emphasis={variant === 'numbers' && cell === 0}>{value}</td>)}</tr>)}</tbody>
        </table>
      </div>
      <div className="grid gap-3 sm:hidden">
        {(block.rows || []).map((row, rowIndex) => <article key={rowIndex} className="editorial-table__card">
          {row.map((value, cell) => <div key={cell}><span>{block.headers?.[cell]}</span><p data-emphasis={variant === 'numbers' && cell === 0}>{value}</p></div>)}
        </article>)}
      </div>
    </section>
  );
}

function Block({ b, i, onMapNodeSelect, onSectionSelect }) {
  b = { ...b, type: String(b.type || '').toLowerCase() };
  if (b.type === 'heading') {
    if (b.level === 3) {
      return <h3 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 pt-4">{b.text}</h3>;
    }
    return <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight pt-4">{b.text}</h2>;
  }
  if (b.type === 'paragraph') {
    return <p className="max-w-[72ch]"><InlineContent text={b.text} links={b.links} onSectionSelect={onSectionSelect} /></p>;
  }
  if (b.type === 'box_remember') {
    return (
      <div data-testid={'box-remember-' + i} className="bg-blue-50 border-l-4 border-blue-600 p-5 sm:p-6 rounded-r-xl">
        <p className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase text-blue-700 font-semibold mb-2">
          <BookmarkCheck size={14} strokeWidth={1.5} /> Da ricordare
        </p>
        <p className="text-base text-slate-800"><InlineContent text={b.text} links={b.links} onSectionSelect={onSectionSelect} /></p>
      </div>
    );
  }
  if (b.type === 'box_warning') {
    return (
      <div data-testid={'box-warning-' + i} className="bg-amber-50 border-l-4 border-amber-500 p-5 sm:p-6 rounded-r-xl">
        <p className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase text-amber-700 font-semibold mb-2">
          <AlertTriangle size={14} strokeWidth={1.5} /> Attenzione
        </p>
        <p className="text-base text-slate-800">{b.text}</p>
      </div>
    );
  }
  if (b.type === 'table') {
    return <EditorialTable block={b} index={i} />;
  }
  if (b.type === 'image') {
    return (
      <figure>
        <ProtectedContentImage url={b.url} alt={b.alt || ''} />
        {b.caption ? <figcaption className="text-sm text-slate-500 mt-2">{b.caption}</figcaption> : null}
      </figure>
    );
  }
  if (b.type === 'concept_map') {
    return <ConceptMap data={b.data} testId={'concept-map-' + i} onNodeSelect={onMapNodeSelect} />;
  }
  if (b.type === 'related_link' && b.section_id) {
    return <button type="button" onClick={() => onSectionSelect?.(b.section_id)} className="inline-flex min-h-11 items-center gap-2 font-semibold text-brand hover:underline" data-testid={`content-link-${i}`}>{b.label || 'Vai alla sezione'} <ArrowRight size={15} /></button>;
  }
  return null;
}

export function DeepDive({ children }) {
  return <details className="deep-dive" data-testid="deep-dive"><summary><span>Approfondimento</span><small>Contenuto opzionale</small></summary><div className="deep-dive__body">{children}</div></details>;
}

function SourceLinks({ sourceIds = [], sources = [] }) {
  const selected = sourceIds.map((id) => sources.find((source) => source.source_id === id)).filter(Boolean);
  if (!selected.length) return null;
  return <div className="mt-4 flex flex-wrap gap-2 text-xs">{selected.map((source) => <div key={source.source_id}>{source.official_url
    ? <a href={source.official_url} target="_blank" rel="noreferrer" className="font-semibold text-brand underline underline-offset-4">Fonte: {source.title}</a>
    : <span className="font-semibold text-slate-500">Fonte: {source.title}</span>}<SourceAttribution source={source}/></div>)}</div>;
}

function StructuredBlock({ block, sources, onSectionSelect }) {
  if (block.type === 'REMEMBER') return <aside className="rounded-r-xl border-l-4 border-blue-600 bg-blue-50 p-5 sm:p-6" data-testid={`editorial-${block.id}`}><h3 className="flex items-center gap-2 text-sm font-bold text-blue-800"><BookmarkCheck size={17}/>Da ricordare</h3><p className="mt-2">{block.content}</p><SourceLinks sourceIds={block.source_ids} sources={sources}/></aside>;
  if (block.type === 'NUMBERS') return <section className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5 sm:p-6" data-testid={`editorial-${block.id}`}><h3 className="flex items-center gap-2 font-display text-xl font-bold text-brand-dark"><Hash size={18}/>Numeri da ricordare</h3><div className="mt-4 grid gap-3 sm:grid-cols-2">{(block.items || []).map((item, index) => <article key={`${item.value}-${index}`} className="rounded-xl bg-white p-4 shadow-sm"><p className="text-xl font-bold text-brand">{item.value}</p><p className="mt-1 text-sm leading-6 text-slate-700">{item.label}</p></article>)}</div><SourceLinks sourceIds={block.source_ids} sources={sources}/></section>;
  if (block.type === 'COMPARISON') return <section className="editorial-table editorial-table--comparison" data-testid={`editorial-${block.id}`}><h3 className="editorial-table__title flex items-center gap-2"><Scale size={18}/>{block.title}</h3><div className="hidden overflow-x-auto sm:block"><table className="w-full text-sm"><thead><tr><th scope="col">Elemento</th>{block.columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr></thead><tbody>{block.rows.map((row) => <tr key={row.label}><th scope="row">{row.label}</th>{row.values.map((value, index) => <td key={index}>{value}</td>)}</tr>)}</tbody></table></div><div className="grid gap-3 sm:hidden">{block.rows.map((row) => <article key={row.label} className="editorial-table__card"><h4 className="font-bold">{row.label}</h4>{row.values.map((value, index) => <div key={index}><span>{block.columns[index]}</span><p>{value}</p></div>)}</article>)}</div><SourceLinks sourceIds={block.source_ids} sources={sources}/></section>;
  if (block.type === 'EXAM_FOCUS') return <aside className="rounded-xl border border-amber-200 bg-amber-50 p-5 sm:p-6" data-testid={`editorial-${block.id}`}><h3 className="flex items-center gap-2 text-sm font-bold text-amber-800"><Target size={17}/>Focus concorso</h3><p className="mt-2">{block.content}</p><SourceLinks sourceIds={block.source_ids} sources={sources}/></aside>;
  if (block.type === 'DEEP_DIVE') return <DeepDive><p>{block.content}</p><SourceLinks sourceIds={block.source_ids} sources={sources}/></DeepDive>;
  if (block.type === 'SEE_ALSO') return <button type="button" onClick={() => onSectionSelect?.(block.target_section_id)} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-brand/20 px-4 font-semibold text-brand hover:bg-brand/5" data-testid={`editorial-${block.id}`}>{block.title || 'Vedi anche'}<ArrowRight size={15}/></button>;
  return null;
}

export function StructuredEditorialBlocks({ blocks = [], sources = [], onSectionSelect }) {
  return <div className="space-y-6" data-testid="structured-editorial-blocks">{[...blocks].sort((a, b) => a.order - b.order).map((block) => <StructuredBlock key={block.id} block={block} sources={sources} onSectionSelect={onSectionSelect}/>)}</div>;
}

export default function ContentBlocks({ blocks, editorialBlocks, sources, sizeClass, onMapNodeSelect, onSectionSelect }) {
  const items = blocks || [];
  const size = sizeClass || 'text-lg';
  return (
    <div className={size + ' space-y-7 leading-[1.78] text-slate-800'}>
      {items.map((b, i) => <Block key={i} b={b} i={i} onMapNodeSelect={onMapNodeSelect} onSectionSelect={onSectionSelect} />)}
      {editorialBlocks?.length ? <StructuredEditorialBlocks blocks={editorialBlocks} sources={sources} onSectionSelect={onSectionSelect}/> : null}
    </div>
  );
}
